"use client";

import { useState, type HTMLAttributes, type MouseEvent } from "react";
import { useAuth } from "@/components/auth-provider";
import {
  getCtaClassName,
  CTA_SIZE_CLASSES,
  CTA_VARIANT_CLASSES,
} from "@/lib/cta-styles";
import type { PiLoginEntryPoint } from "@/lib/analytics/schema";

type AuthButtonProps = HTMLAttributes<HTMLElement> & {
  /** `plain` drops the pill styling and renders with `className` alone. */
  appearance?: "cta" | "plain";
  variant?: keyof typeof CTA_VARIANT_CLASSES;
  size?: keyof typeof CTA_SIZE_CLASSES;
  /**
   * Checkout URL for a specific plan. Renders an anchor so the CTA stays a real link, and becomes
   * the post-auth destination unless the visitor already has an active subscription.
   */
  href?: string;
  /** Without `href`, whether the checkout redirect preselects the base plan or opens it bare. */
  preselectPlan?: boolean;
  /** Attributes the `pi_landing_login_action` events to the CTA that opened the modal. */
  loginEntryPoint: PiLoginEntryPoint;
  /** Plan label reported alongside the login events, for the pricing CTAs. */
  selectedPlan?: string;
  /** Fires the CTA's own click event (`pi_landing_cta_clicked` or `pi_purchase_button_clicked`). */
  trackClick?: () => void;
};

/** CTA that opens the PW login modal, or jumps straight to the app when already signed in. */
export function AuthButton({
  appearance = "cta",
  variant = "primary",
  size = "medium",
  className = "",
  href,
  preselectPlan = true,
  loginEntryPoint,
  selectedPlan,
  trackClick,
  ...props
}: AuthButtonProps) {
  const { goToPiApp } = useAuth();
  const [isPending, setIsPending] = useState(false);

  const handleClick = async (event: MouseEvent<HTMLElement>) => {
    // Let cmd/ctrl/shift-click open the checkout URL in a new tab as any link would.
    const opensNewTab =
      Boolean(href) && (event.metaKey || event.ctrlKey || event.shiftKey);

    // A click landing on the in-flight previous one does nothing, so it is not reported either.
    if (isPending && !opensNewTab) {
      event.preventDefault();
      return;
    }

    trackClick?.();

    if (opensNewTab) {
      return;
    }

    event.preventDefault();
    setIsPending(true);

    try {
      await goToPiApp(href, preselectPlan, {
        entryPoint: loginEntryPoint,
        selectedPlan,
      });
    } finally {
      setIsPending(false);
    }
  };

  const resolvedClassName =
    appearance === "cta"
      ? getCtaClassName({ variant, size, className })
      : className;

  if (href) {
    return (
      <a
        {...props}
        href={href}
        className={resolvedClassName}
        aria-busy={isPending}
        onClick={handleClick}
      />
    );
  }

  return (
    <button
      {...props}
      type="button"
      className={resolvedClassName}
      disabled={isPending}
      aria-busy={isPending}
      onClick={handleClick}
    />
  );
}
